import { Division } from 'components/division';
import { Multiplication } from 'components/multiplication';
import { Equals } from 'components/equals';
import { FormulaDefinition } from './types';
import styles from './formula-renderer.module.scss';

interface FormulaRendererProps {
  formula: FormulaDefinition;
}

/**
 * Renders: A / B = C
 * @param {FormulaRendererProps} props - Component props containing formula
 * @return {JSX.Element} JSX element
 */
const SimpleDivisionFormula = ({ formula }: FormulaRendererProps) => {
  const { labels, resultLabel } = formula;

  return (
    <Equals
      operandOne={
        <Division
          operandOne={labels.numerator}
          operandTwo={labels.denominator}
        />
      }
      operandTwo={resultLabel}
    />
  );
};

/**
 * Renders: A / (B × C) = D
 * Used for ORRI: NRA / (GrossAcres × 8)
 * @param {FormulaRendererProps} props - Component props containing formula
 * @return {JSX.Element} JSX element
 */
const DivisionWithMultiplierFormula = ({ formula }: FormulaRendererProps) => {
  const { labels, resultLabel } = formula;

  return (
    <Equals
      operandOne={
        <Division
          operandOne={labels.numerator}
          operandTwo={
            <Multiplication
              operandOne={labels.denominator1}
              operandTwo={labels.denominator2}
            />
          }
        />
      }
      operandTwo={resultLabel}
    />
  );
};

/**
 * Renders: A / (B × C × D) = E
 * Used for NPRI with NRA: NRA / (GrossAcres × LeaseRoyaltyRate × 8)
 * @param {FormulaRendererProps} props - Component props containing formula
 * @return {JSX.Element} JSX element
 */
const DivisionWithTripleMultiplierFormula = ({
  formula,
}: FormulaRendererProps) => {
  const { labels, resultLabel } = formula;

  const denominator = (
    <span className={styles.operand}>
      {labels.denominator1} × {labels.denominator2} × {labels.denominator3}
    </span>
  );

  return (
    <Equals
      operandOne={
        <Division operandOne={labels.numerator} operandTwo={denominator} />
      }
      operandTwo={resultLabel}
    />
  );
};

/**
 * Renders: ((NRA/Tract Gross Acres) / 8) + Burdens
 *          ─────────────────────────────────────────  = Executive
 *                    Lease Royalty Rate
 * Used for Executive/Bonus/Delay with NRA input
 * @param {FormulaRendererProps} props - Component props containing formula
 * @return {JSX.Element} JSX element
 */
const ComplexNraExecutiveFormula = ({ formula }: FormulaRendererProps) => {
  const { labels, resultLabel } = formula;

  // Numerator as inline text: (({NRA}/{Gross Acres}) / 8) + {Burdens}
  const numerator = (
    <span className={styles.operand}>
      (({labels.nra}/{labels.grossAcres}) / 8) + {labels.royaltyBurdens}
    </span>
  );

  return (
    <Equals
      operandOne={
        <Division operandOne={numerator} operandTwo={labels.leaseRoyaltyRate} />
      }
      operandTwo={resultLabel}
    />
  );
};

/**
 * Renders: A / B - C = D
 * Used for NMA Royalty: NMA / GrossAcres - RoyaltyBurdens
 * @param {FormulaRendererProps} props - Component props containing formula
 * @return {JSX.Element} JSX element
 */
const DivisionMinusFormula = ({ formula }: FormulaRendererProps) => {
  const { labels, resultLabel } = formula;

  return (
    <Equals
      operandOne={
        <div className={styles.horizontalGroup}>
          <Division
            operandOne={labels.numerator}
            operandTwo={labels.denominator}
          />
          <span className={styles.operator}>-</span>
          <span className={styles.operand}>{labels.subtrahend}</span>
        </div>
      }
      operandTwo={resultLabel}
    />
  );
};

/**
 * Renders: ((A / (B × 8)) + C) / D - C = E
 * Used for NRA Royalty: ((NRA / (GrossAcres × 8)) + RoyaltyBurdens) / LeaseRoyaltyRate - RoyaltyBurdens
 * @param {FormulaRendererProps} props - Component props containing formula
 * @return {JSX.Element} JSX element
 */
const ComplexNraRoyaltyFormula = ({ formula }: FormulaRendererProps) => {
  const { labels, resultLabel } = formula;

  // Numerator: (({NRA}/{Gross Acres × 8})) + {Burdens}
  const numerator = (
    <span className={styles.operand}>
      (({labels.nra}/({labels.grossAcres} × 8)) + {labels.royaltyBurdens})
    </span>
  );

  return (
    <Equals
      operandOne={
        <div className={styles.horizontalGroup}>
          <Division
            operandOne={numerator}
            operandTwo={labels.leaseRoyaltyRate}
          />
          <span className={styles.operator}>-</span>
          <span className={styles.operand}>{labels.royaltyBurdens}</span>
        </div>
      }
      operandTwo={resultLabel}
    />
  );
};

/**
 * Renders: A - B = C
 * @param {FormulaRendererProps} props - Component props containing formula
 * @return {JSX.Element} JSX element
 */
const SimpleSubtractionFormula = ({ formula }: FormulaRendererProps) => {
  const { labels, resultLabel } = formula;

  return (
    <Equals
      operandOne={
        <div className={styles.horizontalGroup}>
          <span className={styles.operand}>{labels.operand1}</span>
          <span className={styles.operator}>-</span>
          <span className={styles.operand}>{labels.operand2}</span>
        </div>
      }
      operandTwo={resultLabel}
    />
  );
};

/**
 * Renders: A × B = C
 * @param {FormulaRendererProps} props - Component props containing formula
 * @return {JSX.Element} JSX element
 */
const SimpleMultiplicationFormula = ({ formula }: FormulaRendererProps) => {
  const { labels, resultLabel } = formula;

  return (
    <Equals
      operandOne={
        <Multiplication
          operandOne={labels.operand1}
          operandTwo={labels.operand2}
        />
      }
      operandTwo={resultLabel}
    />
  );
};

/**
 * Renders: A × B × C = D
 * Used for Output NRA: Gross Acres × NRI × 8
 * @param {FormulaRendererProps} props - Component props containing formula
 * @return {JSX.Element} JSX element
 */
const TripleMultiplicationFormula = ({ formula }: FormulaRendererProps) => {
  const { labels, resultLabel } = formula;

  return (
    <Equals
      operandOne={
        <div className={styles.horizontalGroup}>
          <span className={styles.operand}>{labels.operand1}</span>
          <span className={styles.operator}>×</span>
          <span className={styles.operand}>{labels.operand2}</span>
          <span className={styles.operator}>×</span>
          <span className={styles.operand}>{labels.operand3}</span>
        </div>
      }
      operandTwo={resultLabel}
    />
  );
};

/**
 * Renders: A × (1 - B - C) = D
 * Used for WI NRI calculation
 * @param {FormulaRendererProps} props - Component props containing formula
 * @return {JSX.Element} JSX element
 */
const WiNriFormula = ({ formula }: FormulaRendererProps) => {
  const { labels, resultLabel } = formula;

  return (
    <Equals
      operandOne={
        <div className={styles.horizontalGroup}>
          <span className={styles.operand}>{labels.wi}</span>
          <span className={styles.operator}>×</span>
          <span className={styles.operand}>
            (1 - {labels.leaseRoyaltyRate} - {labels.orriBurdens})
          </span>
        </div>
      }
      operandTwo={resultLabel}
    />
  );
};

/**
 * Renders: (A × B) / (1 - C) = D
 * Used for WI Post Burden NMA: (NRI × Gross Acres) / (1 - Lease Royalty Rate)
 * @param {FormulaRendererProps} props - Component props containing formula
 * @return {JSX.Element} JSX element
 */
const WiPostBurdenNmaFormula = ({ formula }: FormulaRendererProps) => {
  const { labels, resultLabel } = formula;

  const numerator = (
    <span className={styles.operand}>
      {labels.nri} × {labels.grossAcres}
    </span>
  );

  const denominator = (
    <span className={styles.operand}>1 - {labels.leaseRoyaltyRate}</span>
  );

  return (
    <Equals
      operandOne={<Division operandOne={numerator} operandTwo={denominator} />}
      operandTwo={resultLabel}
    />
  );
};

// Simple passthrough formula: A = B (e.g., NRI = ORRI)
const SimplePassthroughFormula = ({ formula }: FormulaRendererProps) => {
  const { labels, resultLabel } = formula;

  return <Equals operandOne={labels.operand1} operandTwo={resultLabel} />;
};

export const FormulaRenderer = ({ formula }: FormulaRendererProps) => {
  switch (formula.type) {
    case 'simple_division':
      return <SimpleDivisionFormula formula={formula} />;
    case 'division_with_multiplier':
      return <DivisionWithMultiplierFormula formula={formula} />;
    case 'division_with_triple_multiplier':
      return <DivisionWithTripleMultiplierFormula formula={formula} />;
    case 'complex_nra_executive':
      return <ComplexNraExecutiveFormula formula={formula} />;
    case 'division_minus':
      return <DivisionMinusFormula formula={formula} />;
    case 'complex_nra_royalty':
      return <ComplexNraRoyaltyFormula formula={formula} />;
    case 'simple_subtraction':
      return <SimpleSubtractionFormula formula={formula} />;
    case 'simple_multiplication':
      return <SimpleMultiplicationFormula formula={formula} />;
    case 'triple_multiplication':
      return <TripleMultiplicationFormula formula={formula} />;
    case 'simple_passthrough':
      return <SimplePassthroughFormula formula={formula} />;
    case 'wi_nri':
      return <WiNriFormula formula={formula} />;
    case 'wi_post_burden_nma':
      return <WiPostBurdenNmaFormula formula={formula} />;
    default:
      return null;
  }
};
